import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row
} from 'reactstrap';
import { InputField } from 'src/components';
import { setFromErrors } from 'src/helpers';
import { useAppSelector } from 'src/rtk/app/hook';
import { useCreateInterviewScheduleMutation, useLazyGetEvaluatedTraineeListQuery } from 'src/rtk/features/logbookMonitoringReport/logbookMonitoringReportApi';
import { useLazyGetDepartmentsByInstituteQuery } from 'src/rtk/features/setup/department/departmentApi';
import { useGetOrganizationsQuery } from 'src/rtk/features/setup/organization/setupOrganizationApi';
import { useLazyGetSupervisorsByDepartmentQuery } from 'src/rtk/features/trainingRecord/trainingRecordlApi';
import loader from '../../assets/images/loader.gif';


interface ScheduleCreate {
  interview_id: string;
  session_name: string;
  session_year: string;
  internal_examiner_id: number;
  external_examiner_id: number;
  interview_date: string;
  interview_time: string;
  interview_venue: string;
  institute_id: string;
  department_id: string;
  trainees: { reg_no: string }[];
}

const ScheduleEditModal = (props: any) => {
  const { isOpen, toggle, editData } = props;

  const authUser = useAppSelector((state) => state.auth.user);

  const [getEvaluatedTraineeList, { data: evaluatedTraineeData }] = useLazyGetEvaluatedTraineeListQuery();
  const [createInterviewSchedule, { isLoading, isSuccess, isError, error }] = useCreateInterviewScheduleMutation();

  const err: CustomError = error as CustomError;

  const [sessionName, setSessionName] = useState('');
  const [sessionYear, setSessionYear] = useState('');

  const { data: instituteLookupData } = useGetOrganizationsQuery();

  const allInstitute: SelectOption[] =
    instituteLookupData?.data?.map((institute) => ({
      value: institute.id!,
      label: institute.organization_name,
    })) || [];

  const currentYear = new Date().getFullYear();
  const startYear = 2025;

  const years = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push(year);
  }

  const allYears: SelectOption[] = years?.map((item: any) => ({
    value: item,
    label: item,
  })) || [];

  const courseSession: SelectOption[] = [
    { value: 'January-June', label: 'January-June' },
    { value: 'July-December', label: 'July-December' },
  ];

  const [selectedInstitue, setSelectedInstitute] = useState<any>('');
  const [selectedDepartment, setSelectedDepartment] = useState<any>('');
  const [selectedSupervisor, setSelectedSupervisor] = useState<any>('');
  const [departmentName, setDepartmentName] = useState('');
  const [instituteForDeparment, setInstituteForDeparment] = useState('');

  const [getDepartmentsByInstitute, { data: departmentLookupData }] = useLazyGetDepartmentsByInstituteQuery();
  const [getSupervisorsByDepartment, { data: supervisorData }] = useLazyGetSupervisorsByDepartmentQuery();

  // Refs to track if we've already set values from editData
  const hasSetInstitute = useRef(false);
  const hasSetDepartment = useRef(false);
  const hasSetSupervisor = useRef(false);

  useEffect(() => {
    if (instituteForDeparment !== '') {
      getDepartmentsByInstitute(instituteForDeparment);
    }
  }, [instituteForDeparment]);

  const allDepartment: SelectOption[] =
    departmentLookupData?.data?.map((department) => ({
      value: department.id!,
      label: department.department_name,
    })) || [];

  const allSupervisor: SelectOption[] =
    supervisorData?.data?.map((supervisor) => ({
      value: supervisor.user_id!,
      label: supervisor.full_name!,
    })) || [];

  const initialValues: ScheduleCreate = {
    interview_id: '',
    session_name: '',
    session_year: '',
    internal_examiner_id: 0,
    external_examiner_id: 0,
    interview_date: '',
    interview_time: '',
    interview_venue: '',
    institute_id: '',
    department_id: '',
    trainees: [],
  };

  const formik = useFormik({
    initialValues,

    onSubmit: (values) => {
      values.internal_examiner_id = authUser?.id!;
      console.log(values);
      createInterviewSchedule(values);
    },
  });

  const {
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setValues,
    handleChange,
    values,
    touched,
    errors,
    resetForm,
    setErrors,
  } = formik;


  useEffect(() => {
    if (sessionName && sessionYear) {
      const queryUrl = `session_name=${sessionName}&session_year=${sessionYear}`;
      getEvaluatedTraineeList(queryUrl);
    }
  }, [sessionName, sessionYear]);

  useEffect(() => {
    if (selectedInstitue !== '') {
      setFieldValue('institute_id', selectedInstitue?.value);
      setInstituteForDeparment(selectedInstitue?.label);
    }
  }, [selectedInstitue]);

  useEffect(() => {
    if (selectedDepartment !== '') {
      setFieldValue('department_id', selectedDepartment?.value);
      setDepartmentName(selectedDepartment?.label);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    if (selectedSupervisor !== '') {
      setFieldValue('external_examiner_id', selectedSupervisor?.value);
    }
  }, [selectedSupervisor]);

  useEffect(() => {
    if (evaluatedTraineeData?.data?.length) {
      const traineesArray = evaluatedTraineeData.data.map((trainee: any) => ({
        reg_no: trainee?.logbook_entry_master?.registration_user_info?.registration_no || '',
      }));
      setFieldValue('trainees', traineesArray);
    }
  }, [evaluatedTraineeData]);

  useEffect(() => {
    if (selectedInstitue?.value && selectedDepartment?.label) {
      getSupervisorsByDepartment({
        institute_id: selectedInstitue?.value,
        department_name: selectedDepartment?.label
      });
    }
  }, [selectedInstitue, selectedDepartment]);

  // Populate form with editData when modal opens
  useEffect(() => {
    if (editData && isOpen && allInstitute.length > 0) {
      // Reset refs when modal opens with new data
      hasSetInstitute.current = false;
      hasSetDepartment.current = false;
      hasSetSupervisor.current = false;

      // Set form values
      setValues({
        interview_id: editData.id || '',
        session_name: editData.session_name || '',
        session_year: editData.session_year || '',
        internal_examiner_id: editData.internal_examiner_id || 0,
        external_examiner_id: editData.external_examiner_id || 0,
        interview_date: editData.interview_date || '',
        interview_time: editData.interview_time || '',
        interview_venue: editData.interview_venue || '',
        institute_id: editData.institute_id || '',
        department_id: editData.department_id || '',
        trainees: editData.participants?.map((p: any) => ({ reg_no: p.trainee_reg_no })) || [],
      });

      // Set session name and year for trainee list fetch
      setSessionName(editData.session_name || '');
      setSessionYear(editData.session_year || '');

      // Set institute selection
      const instituteOption = allInstitute.find(inst => inst.value === editData.institute_id);
      if (instituteOption && !hasSetInstitute.current) {
        setSelectedInstitute(instituteOption);
        hasSetInstitute.current = true;

        // Trigger department fetch
        if (editData.institute_id) {
          getDepartmentsByInstitute(instituteOption.label);
        }
      }
    }
  }, [editData?.id, isOpen, allInstitute.length]);

  // Set department after departments are loaded
  useEffect(() => {
    if (editData && isOpen && departmentLookupData?.data && allDepartment.length > 0 && !hasSetDepartment.current) {
      const departmentOption = allDepartment.find(dept => dept.value === editData.department_id);
      if (departmentOption) {
        setSelectedDepartment(departmentOption);
        hasSetDepartment.current = true;

        // Trigger supervisor fetch
        if (selectedInstitue?.value) {
          getSupervisorsByDepartment({
            institute_id: selectedInstitue.value,
            department_name: departmentOption.label
          });
        }
      }
    }
  }, [departmentLookupData?.data, editData?.id, isOpen, allDepartment.length]);

  // Set external examiner after supervisors are loaded
  useEffect(() => {
    if (editData && isOpen && supervisorData?.data && allSupervisor.length > 0 && !hasSetSupervisor.current) {
      const supervisorOption = allSupervisor.find(sup => sup.value === editData.external_examiner_id);
      if (supervisorOption) {
        setSelectedSupervisor(supervisorOption);
        hasSetSupervisor.current = true;
      }
    }
  }, [supervisorData?.data, editData?.id, isOpen, allSupervisor.length]);

  // Reset everything when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setSelectedInstitute('');
      setSelectedDepartment('');
      setSelectedSupervisor('');
      setSessionName('');
      setSessionYear('');
      hasSetInstitute.current = false;
      hasSetDepartment.current = false;
      hasSetSupervisor.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Interview Schedule Updated Successfully');
      resetForm();
      setSelectedInstitute('');
      setSelectedDepartment('');
      setSelectedSupervisor('');
      toggle();
    }
    if (isError) {
      toast.error(err?.message || 'Something Went Wrong!');
      setFromErrors(err.data, setErrors);
    }
  }, [isSuccess, isError]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      {isLoading ? (
        <div className="overlay">
          <img src={loader} alt="Loading" height={100} width={300} />
        </div>
      ) : null}
      <ModalHeader toggle={toggle}>Create Interview Schedule</ModalHeader>
      <ModalBody>
        <div className="gap-2 w-100">
          <Form
            className="custom-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            onReset={() => {
              resetForm();
              setSelectedInstitute('');
              setSelectedDepartment('');
              setSelectedSupervisor('');
              setSessionName('');
              setSessionYear('');
            }}
          >
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="session_name">Session Name</Label>
                  <InputField
                    name='session_name'
                    type="select"
                    placeholder="Session Name"
                    options={courseSession}
                    value={values.session_name}
                    onChange={(e: any) => {
                      handleChange(e);
                      setSessionName(e.target.value);
                    }}
                    disabled
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="session_year">Session Year</Label>
                  <InputField
                    name='session_year'
                    type="select"
                    placeholder="Session Year"
                    options={allYears}
                    value={values.session_year}
                    onChange={(e: any) => {
                      handleChange(e);
                      setSessionYear(e.target.value);
                    }}
                    disabled
                  />
                </FormGroup>
              </Col>
              <Col md={12} className='mb-3'>
                <div className='block-heading mb-1'>Trainee List</div>
                {evaluatedTraineeData?.data?.length > 0 ? (
                  <Row>
                    {evaluatedTraineeData?.data?.map((trainee: any) => (
                      <Col key={trainee.id} md="3" sm="6" xs="12">
                        <Card className="shadow-sm">
                          <CardBody>
                            <div className="text-center fw-bold">
                              {trainee?.logbook_entry_master?.registration_user_info?.personal_info?.full_name} <br />
                              BMDC : {trainee?.logbook_entry_master?.registration_user_info?.bmdc_registration_no}
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : 
                (editData?.participants?.length > 0) ? (
                  <Row>
                    {editData?.participants?.map((trainee: any) => (
                      <Col key={trainee.id} md="3" sm="6" xs="12">
                        <Card className="shadow-sm">
                          <CardBody>
                            <div className="text-center fw-bold">
                              {trainee?.registration?.personal_info?.full_name} <br />
                              BMDC : {trainee?.registration?.bmdc_registration_no}
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) :
                (
                  <div className='border border-2 border-light p-2 text-center fw-bold text-danger'>No Trainee Found</div>
                )}
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label for="venue" className='required-field'>Venue</Label>
                  <InputField
                    type="text"
                    placeholder="Venue"
                    {...getFieldProps('interview_venue')}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label for="date" className='required-field'>Date</Label>
                  <InputField
                    type="date"
                    placeholder="Date"
                    {...getFieldProps('interview_date')}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label for="time" className='required-field'>Time</Label>
                  <InputField
                    type="time"
                    placeholder="Time"
                    {...getFieldProps('interview_time')}
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <div className='mb-3'>
                  <Label className="form-label required-field">Institute</Label>
                  <Select
                    isClearable={true}
                    isSearchable={true}
                    value={selectedInstitue}
                    onChange={setSelectedInstitute}
                    options={allInstitute}
                    placeholder='Select Institute'
                    name="institute_id"
                    menuPortalTarget={document.body}
                    menuPosition={'fixed'}
                    styles={{ menuPortal: (provided: any, state: any) => ({ ...provided, zIndex: '9999', }) }}
                  />
                </div>
              </Col>

              <Col md={4}>
                <div className='mb-3'>
                  <Label className="form-label required-field">Department</Label>
                  <Select
                    isClearable={true}
                    isSearchable={true}
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                    options={allDepartment}
                    placeholder='Select Department'
                    name="department_id"
                    menuPortalTarget={document.body}
                    menuPosition={'fixed'}
                    styles={{ menuPortal: (provided: any, state: any) => ({ ...provided, zIndex: '9999', }) }}
                  />
                  {errors.department_id && touched.department_id ? (
                    <div className="text-danger">{errors.department_id}</div>
                  ) : null}
                </div>
              </Col>

              <Col md={4}>
                <div className='mb-3'>
                  <Label className="form-label required-field">External Examiner</Label>
                  <Select
                    isClearable={true}
                    isSearchable={true}
                    value={selectedSupervisor}
                    onChange={setSelectedSupervisor}
                    options={allSupervisor}
                    placeholder='Select..'
                    name="external_examiner_id"
                    menuPortalTarget={document.body}
                    menuPosition={'fixed'}
                    styles={{ menuPortal: (provided: any, state: any) => ({ ...provided, zIndex: '9999', }) }}
                  />
                  {errors.external_examiner_id && touched.external_examiner_id ? (
                    <div className="text-danger">{errors.external_examiner_id}</div>
                  ) : null}
                </div>
              </Col>

              <Col>
                <div className="d-grid gap-2 d-flex justify-content-center mt-3">
                  <Button color="danger" type="reset">
                    Reset
                  </Button>

                  <Button
                    color="success"
                    type="submit"
                    disabled={isLoading}
                  >
                    Submit
                  </Button>
                </div>
              </Col>

            </Row>



          </Form>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ScheduleEditModal;